export interface CityData {
  slug: string;
  name: string;
  region: string;
  tagline: string;
  heroImage: string;
  marketStats: {
    avgRentalYield: string;
    vacancyRate: string;
    medianRent: string;
    medianHousePrice: string;
  };
  overview: string;
  keySuburbs: string[];
  marketInsights: { title: string; detail: string }[];
  whyUs: string;
}

export const cityData: Record<string, CityData> = {
  "hervey-bay": {
    slug: "hervey-bay",
    name: "Hervey Bay",
    region: "Wide Bay–Burnett",
    tagline: "Queensland's fastest-growing coastal city — and one of the state's strongest rental markets.",
    heroImage: "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1920&q=80",
    marketStats: {
      avgRentalYield: "5.8%",
      vacancyRate: "1.2%",
      medianRent: "$520/wk",
      medianHousePrice: "$590,000",
    },
    overview:
      "Hervey Bay has transformed from a sleepy coastal town into one of Queensland's most sought-after investment destinations. Driven by sea-change migration, retiree demand, and its gateway status to Fraser Island (K'gari), the city consistently delivers tight vacancy rates and strong yield performance. One Property Australia manages residential and short-term rental portfolios across Hervey Bay and surrounding areas including Maryborough, Howard, and Torbanlea.",
    keySuburbs: ["Urangan", "Pialba", "Scarness", "Torquay", "Point Vernon", "Eli Waters", "Kawungan"],
    marketInsights: [
      {
        title: "Sea-Change Migration",
        detail: "Hervey Bay continues to attract retirees and remote workers from Brisbane and Sydney, keeping demand for quality rental properties consistently ahead of supply.",
      },
      {
        title: "Holiday & Short-Term Rental Opportunity",
        detail: "As the gateway to K'gari (Fraser Island), Hervey Bay supports a strong short-term rental market. We help investors maximise returns across both permanent and holiday letting strategies.",
      },
      {
        title: "Infrastructure Growth",
        detail: "Ongoing investment in the Hervey Bay Hospital precinct, the Urangan Boat Harbour upgrade, and Stockland Hervey Bay expansion is driving long-term population and rental demand growth.",
      },
      {
        title: "Tight Vacancy Rates",
        detail: "Hervey Bay regularly records vacancy rates below 1.5%, meaning well-managed properties rarely sit empty. Our proactive tenant acquisition process keeps your property earning.",
      },
    ],
    whyUs:
      "Our local property managers understand the Hervey Bay market intimately — from seasonal holiday let fluctuations to the specific maintenance demands of coastal properties. We keep your investment in peak condition and your returns optimised year-round.",
  },

  "rockhampton": {
    slug: "rockhampton",
    name: "Rockhampton",
    region: "Central Queensland",
    tagline: "The beef capital of Australia — with a resilient, yield-driven property market to match.",
    heroImage: "https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=1920&q=80",
    marketStats: {
      avgRentalYield: "6.4%",
      vacancyRate: "1.8%",
      medianRent: "$440/wk",
      medianHousePrice: "$380,000",
    },
    overview:
      "Rockhampton is Central Queensland's commercial, government, and services hub — and consistently one of Australia's highest-yielding property markets. Underpinned by agriculture, government employment, the defence sector, and proximity to Gladstone's industrial economy, Rocky's rental market rewards investors who manage their properties well. One Property Australia handles residential portfolios across Rockhampton and the surrounding region including Gracemere, Mount Morgan, and the Capricorn Coast.",
    keySuburbs: ["Gracemere", "Berserker", "Kawana", "Norman Gardens", "Allenstown", "Frenchville", "Depot Hill"],
    marketInsights: [
      {
        title: "Consistently High Yields",
        detail: "With median house prices well below Brisbane but solid rental demand, Rockhampton delivers gross yields that regularly exceed 6% — rare in a city of its size and stability.",
      },
      {
        title: "Government & Defence Employment Base",
        detail: "RAAF Base Rockhampton and substantial state government employment provide a stable, reliable tenant demographic that typically means longer tenancies and lower turnover.",
      },
      {
        title: "Agricultural Economy",
        detail: "Central Queensland's beef and agriculture sector generates consistent regional economic activity, supporting employment and rental demand across Rockhampton's catchment.",
      },
      {
        title: "Gracemere Growth Corridor",
        detail: "The Gracemere suburb and surrounding Rockhampton Regional growth corridor is seeing significant residential development, attracting families seeking affordable quality housing within commuting distance.",
      },
    ],
    whyUs:
      "We know Central Queensland. Our property managers understand the rhythms of Rockhampton's market — including the distinct needs of government, agricultural, and defence tenants — and tailor our management approach to deliver consistent occupancy and minimal maintenance surprises.",
  },

  "mackay": {
    slug: "mackay",
    name: "Mackay",
    region: "Mackay–Isaac–Whitsunday",
    tagline: "North Queensland's economic powerhouse — where mining meets lifestyle and strong rental yields.",
    heroImage: "https://images.unsplash.com/photo-1518623489648-a173ef7824f3?w=1920&q=80",
    marketStats: {
      avgRentalYield: "6.1%",
      vacancyRate: "1.5%",
      medianRent: "$530/wk",
      medianHousePrice: "$475,000",
    },
    overview:
      "Mackay is the service hub for the Bowen Basin — Australia's most productive coking coal region — and one of Queensland's most compelling regional investment markets. After cyclical softness following the mining boom, Mackay has re-emerged with tight vacancy rates, rising rents, and a diversified economy that includes agriculture, tourism, and major port activity. One Property Australia manages residential properties across Mackay and the wider region including Sarina, Proserpine, and Airlie Beach.",
    keySuburbs: ["North Mackay", "Andergrove", "Beaconsfield", "Mount Pleasant", "Eimeo", "Rural View", "Blacks Beach"],
    marketInsights: [
      {
        title: "Mining Sector Recovery",
        detail: "With coking coal demand from Asia remaining strong and BHP and Glencore expanding operations in the Bowen Basin, Mackay's economy is on a sustained upswing — directly benefiting rental demand.",
      },
      {
        title: "Corporate Accommodation Demand",
        detail: "Major resource companies require quality corporate accommodation for FIFO and residential employees. Well-maintained properties in key suburbs attract corporate tenancies with longer lease terms.",
      },
      {
        title: "Coastal Lifestyle Suburbs",
        detail: "Suburbs like Eimeo, Rural View, and Blacks Beach deliver dual appeal: strong rental demand from the local workforce and lifestyle value that underpins long-term capital growth.",
      },
      {
        title: "Whitsunday Proximity",
        detail: "Mackay serves as the southern gateway to the Whitsundays, supporting tourism and hospitality employment that broadens the local rental tenant base beyond the resources sector.",
      },
    ],
    whyUs:
      "Mackay's resource-sector cycles require experienced property management. We understand corporate tenancy requirements, FIFO accommodation standards, and the maintenance demands of tropical coastal properties — keeping your asset in top condition through every market phase.",
  },

  "townsville": {
    slug: "townsville",
    name: "Townsville",
    region: "North Queensland",
    tagline: "North Queensland's capital city — a deep, diverse, and demand-driven rental market.",
    heroImage: "https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?w=1920&q=80",
    marketStats: {
      avgRentalYield: "5.9%",
      vacancyRate: "1.4%",
      medianRent: "$490/wk",
      medianHousePrice: "$430,000",
    },
    overview:
      "Townsville is North Queensland's largest city and a major centre for defence, healthcare, education, and mining services. Home to Lavarack Barracks — Australia's largest Army base — James Cook University, and a major regional hospital, the city supports a large, stable, and diverse rental population. One Property Australia manages residential portfolios across Townsville and the surrounding region including Charters Towers, Ingham, and Palm Island services.",
    keySuburbs: ["Kirwan", "Thuringowa Central", "Annandale", "Idalia", "Rasmussen", "Cranbrook", "Hyde Park"],
    marketInsights: [
      {
        title: "Military Tenant Base",
        detail: "Lavarack Barracks houses thousands of ADF personnel and their families. Defence Housing Australia (DHA) and private military tenants are among the most reliable and well-resourced tenant demographics in Australia.",
      },
      {
        title: "University & Healthcare Employment",
        detail: "James Cook University and the Townsville University Hospital together employ thousands and generate consistent demand for quality rental accommodation from academics, medical staff, and students.",
      },
      {
        title: "Infrastructure Investment",
        detail: "The Townsville City Deal has triggered major infrastructure investment including the Haughton Pipeline, Port of Townsville channel widening, and stadium precinct development — driving population growth and rental demand.",
      },
      {
        title: "Kirwan Growth Corridor",
        detail: "Kirwan and Thuringowa continue to attract families seeking affordable, well-located homes within reach of the Ring Road and major employment nodes — a consistently high-demand rental precinct.",
      },
    ],
    whyUs:
      "We understand Townsville's unique tenant mix. Our property managers are experienced in DHA-adjacent private rentals, university accommodation, and the specific needs of North Queensland's tropical property market — from cyclone compliance to pest management.",
  },

  "ipswich": {
    slug: "ipswich",
    name: "Ipswich",
    region: "South East Queensland",
    tagline: "One of Australia's fastest-growing cities — where infrastructure investment meets rental demand.",
    heroImage: "https://images.unsplash.com/photo-1486325212027-8081e485255e?w=1920&q=80",
    marketStats: {
      avgRentalYield: "5.2%",
      vacancyRate: "1.1%",
      medianRent: "$520/wk",
      medianHousePrice: "$570,000",
    },
    overview:
      "Ipswich is the fastest-growing local government area in Queensland and among the fastest nationally. Positioned 40 minutes west of Brisbane CBD with strong transport links, the Ipswich LGA is experiencing unprecedented residential development across master-planned communities in Ripley Valley, Springfield, and beyond. One Property Australia manages residential portfolios across Ipswich City and the broader Scenic Rim and Lockyer Valley corridors.",
    keySuburbs: ["Springfield Lakes", "Ripley", "Redbank Plains", "Collingwood Park", "Ipswich CBD", "Leichhardt", "Yamanto"],
    marketInsights: [
      {
        title: "Population Growth Trajectory",
        detail: "The Ipswich LGA is projected to reach 500,000 residents by 2041 — up from approximately 240,000 today. This structural demand underpins long-term rental market strength across all property types.",
      },
      {
        title: "Ripley Valley Masterplan",
        detail: "Ripley Valley is one of Queensland's largest urban development projects, delivering thousands of new dwellings with parks, schools, and retail. Properties here attract strong family tenant demand.",
      },
      {
        title: "Transport & Employment Access",
        detail: "Ipswich is served by rail, major motorways, and is home to RAAF Base Amberley — one of Australia's largest air force bases — providing a stable military employment base that supports rental demand.",
      },
      {
        title: "Affordability Relative to Brisbane",
        detail: "Ipswich continues to offer meaningfully lower entry prices than Brisbane with comparable rental yields — attracting investors seeking yield without the premium required in inner Brisbane suburbs.",
      },
    ],
    whyUs:
      "Ipswich's growth means new rental stock is constantly entering the market. Our proactive tenant acquisition, thorough vetting, and regular property reviews keep your investment competitive and consistently tenanted in a market where landlord diligence makes the difference.",
  },

  "toowoomba": {
    slug: "toowoomba",
    name: "Toowoomba",
    region: "Darling Downs",
    tagline: "The Garden City of Queensland — a stable, high-yield regional property market with a diversified economy.",
    heroImage: "https://images.unsplash.com/photo-1600585154526-990dced4db0d?w=1920&q=80",
    marketStats: {
      avgRentalYield: "5.7%",
      vacancyRate: "1.3%",
      medianRent: "$480/wk",
      medianHousePrice: "$510,000",
    },
    overview:
      "Toowoomba is Queensland's second-largest inland city and a major regional economic hub for agriculture, logistics, healthcare, and education. The University of Southern Queensland, Darling Downs Health, and a thriving agricultural processing sector create a broad, stable employment base that supports consistent rental demand. One Property Australia manages residential portfolios across the Toowoomba Regional LGA including Gatton, Pittsworth, and Warwick.",
    keySuburbs: ["East Toowoomba", "South Toowoomba", "Harristown", "Wilsonton", "Newtown", "Rangeville", "Darling Heights"],
    marketInsights: [
      {
        title: "Inland Hub with Strong Logistics Sector",
        detail: "The Inland Rail project, connecting Melbourne to Brisbane via Toowoomba's Inland Port, is a transformative infrastructure investment cementing the city's role as a national logistics centre.",
      },
      {
        title: "University Town Demographics",
        detail: "The University of Southern Queensland generates consistent demand for rental properties from students, academics, and healthcare workers at the adjacent Toowoomba Hospital precinct.",
      },
      {
        title: "Agricultural Economy",
        detail: "The Darling Downs is Australia's richest agricultural region. Agricultural employment and agribusiness services keep Toowoomba's economy fundamentally grounded — resilient through economic cycles.",
      },
      {
        title: "Lifestyle Appeal",
        detail: "Toowoomba's cooler climate, tree-lined streets, and proximity to Brisbane (90 minutes) make it increasingly attractive to remote-working families seeking affordability and quality of life.",
      },
    ],
    whyUs:
      "Toowoomba's diverse tenant base — from university students and medical staff to agricultural professionals and families — requires nuanced property management. We know the city's distinct suburb characters and match properties with the right tenants for long, stable tenancies.",
  },

  "north-brisbane": {
    slug: "north-brisbane",
    name: "North Brisbane",
    region: "Greater Brisbane",
    tagline: "Brisbane's thriving northern corridor — from the inner ring to Moreton Bay and the hinterland.",
    heroImage: "https://images.unsplash.com/photo-1494522855154-9297ac14b55f?w=1920&q=80",
    marketStats: {
      avgRentalYield: "4.8%",
      vacancyRate: "0.9%",
      medianRent: "$620/wk",
      medianHousePrice: "$780,000",
    },
    overview:
      "North Brisbane encompasses one of Australia's most dynamic residential corridors — from the inner-ring suburbs of Chermside, Nundah, and Kedron through to the rapidly expanding Moreton Bay Region. Proximity to Brisbane Airport, the Prince Charles Hospital campus, and major transport arterials drives extraordinary rental demand across a wide range of property types and price points. One Property Australia manages residential portfolios across Brisbane's northern suburbs and the Moreton Bay LGA.",
    keySuburbs: ["Chermside", "Nundah", "Kedron", "North Lakes", "Redcliffe", "Deception Bay", "Kallangur"],
    marketInsights: [
      {
        title: "Airport & Logistics Employment",
        detail: "Brisbane Airport — Australia's third-busiest — employs thousands and generates consistent demand for proximity rental accommodation across Kedron, Nundah, and the northern suburbs.",
      },
      {
        title: "Healthcare Precinct Demand",
        detail: "The Prince Charles Hospital, Holy Spirit Northside, and The Prince Charles Hospital Foundation create significant healthcare employment driving long-term rental demand in Chermside and Kedron.",
      },
      {
        title: "Moreton Bay Growth Engine",
        detail: "North Lakes, Kallangur, and the Moreton Bay Region are among Australia's fastest-growing residential areas, with Moreton Bay University opening in 2025 as a major demand catalyst.",
      },
      {
        title: "Sub-1% Vacancy",
        detail: "North Brisbane's vacancy rate has compressed to sub-1% — a landlord's market where well-managed properties attract multiple quality applicants and command premium rents.",
      },
    ],
    whyUs:
      "In a sub-1% vacancy market, the difference between landlords is the quality of their property management. Our proactive maintenance, rigorous tenant vetting, and strong rent review process ensures your North Brisbane investment maximises its market position.",
  },

  "south-brisbane": {
    slug: "south-brisbane",
    name: "South Brisbane",
    region: "Greater Brisbane",
    tagline: "Brisbane's high-demand southern suburbs — from the inner city to Logan and the Scenic Rim.",
    heroImage: "https://images.unsplash.com/photo-1508193638397-1c4234db14d8?w=1920&q=80",
    marketStats: {
      avgRentalYield: "4.9%",
      vacancyRate: "1.0%",
      medianRent: "$610/wk",
      medianHousePrice: "$760,000",
    },
    overview:
      "South Brisbane and the broader southern corridor encompasses a rich mix of established inner-city suburbs, multicultural hubs, and expanding outer-ring growth areas. From Sunnybank and Eight Mile Plains through to Springwood, Logan, and Beenleigh, the southern corridor offers investors a wide spectrum of property types and rental demographics — from young professionals and families to students and healthcare workers. One Property Australia manages residential portfolios across Brisbane's southern suburbs and the Logan City LGA.",
    keySuburbs: ["Sunnybank", "Eight Mile Plains", "Moorooka", "Underwood", "Springwood", "Beenleigh", "Loganholme"],
    marketInsights: [
      {
        title: "Multicultural Demand Base",
        detail: "South Brisbane's diverse, multicultural communities — particularly in Sunnybank and Sunnybank Hills — generate strong, community-anchored rental demand with typically long tenancy durations.",
      },
      {
        title: "Logan Affordability & Growth",
        detail: "The Logan City LGA consistently delivers above-Brisbane-average rental yields with lower entry prices. Infrastructure investment and population growth make Logan one of SEQ's most compelling investment corridors.",
      },
      {
        title: "Technology & Business Park Employment",
        detail: "The Eight Mile Plains technology corridor and Springwood business parks are established white-collar employment zones generating professional-grade rental demand in nearby suburbs.",
      },
      {
        title: "Olympic Infrastructure Investment",
        detail: "The 2032 Brisbane Olympics is driving infrastructure investment across the southern corridor, including transport upgrades that directly benefit rental property accessibility and value.",
      },
    ],
    whyUs:
      "South Brisbane's diversity requires tailored property management. We understand the distinct character of each southern suburb — from the family-oriented outer ring to the professional inner south — and apply management strategies that match your property to the right tenant cohort.",
  },

  "sunshine-coast": {
    slug: "sunshine-coast",
    name: "Sunshine Coast",
    region: "Sunshine Coast",
    tagline: "Australia's fastest-growing major city — where lifestyle demand meets transformative infrastructure investment.",
    heroImage: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1920&q=80",
    marketStats: {
      avgRentalYield: "4.7%",
      vacancyRate: "0.8%",
      medianRent: "$680/wk",
      medianHousePrice: "$870,000",
    },
    overview:
      "The Sunshine Coast is one of Australia's fastest-growing regions and the country's 9th-largest city by population. Driven by lifestyle migration from Brisbane and southern states, major infrastructure investment, and the opening of Sunshine Coast University Hospital, the region's rental market is characterised by extraordinary demand, sub-1% vacancy, and consistently rising rents. One Property Australia manages residential portfolios across the Sunshine Coast from Caloundra to Noosa and the Hinterland.",
    keySuburbs: ["Maroochydore", "Caloundra", "Buderim", "Noosa Heads", "Nambour", "Kawana Waters", "Sippy Downs"],
    marketInsights: [
      {
        title: "Sunshine Coast Airport Expansion",
        detail: "The Sunshine Coast Airport international terminal expansion has established direct international routes, further enhancing the region's appeal to both residents and investors.",
      },
      {
        title: "University Hospital Employment",
        detail: "The Sunshine Coast University Hospital is now one of the region's largest employers, generating sustained healthcare professional rental demand particularly in Kawana and Sippy Downs.",
      },
      {
        title: "Digital Nomad & Remote Work Migration",
        detail: "Post-pandemic remote work patterns have permanently increased the Sunshine Coast's population base, with professionals choosing lifestyle over proximity to Brisbane CBD — a lasting structural demand shift.",
      },
      {
        title: "Sub-1% Vacancy",
        detail: "With vacancy consistently below 1%, the Sunshine Coast is one of Australia's tightest rental markets. Quality properties managed well rarely sit empty for more than a few days between tenancies.",
      },
    ],
    whyUs:
      "The Sunshine Coast's extraordinary growth means rental market conditions change rapidly. Our deep local market knowledge, proactive rent reviews, and strong tenant relationships keep your investment positioned at the front of the market — not chasing it.",
  },

  "gold-coast": {
    slug: "gold-coast",
    name: "Gold Coast",
    region: "Gold Coast",
    tagline: "Australia's tourism capital — a deep, diverse rental market with strong long-term and short-term demand.",
    heroImage: "https://images.unsplash.com/photo-1526481280693-3bfa7568e0f3?w=1920&q=80",
    marketStats: {
      avgRentalYield: "4.9%",
      vacancyRate: "1.0%",
      medianRent: "$660/wk",
      medianHousePrice: "$850,000",
    },
    overview:
      "The Gold Coast is Australia's sixth-largest city and its tourism capital — a market that rewards sophisticated property management. Beyond the beachfront towers, the Gold Coast's residential hinterland, growing health and education precincts, and expanding outer suburbs deliver strong long-term rental yields. One Property Australia manages residential portfolios across the Gold Coast from Coomera and Hope Island in the north through to Coolangatta and Tweed Heads in the south.",
    keySuburbs: ["Surfers Paradise", "Broadbeach", "Robina", "Coomera", "Varsity Lakes", "Burleigh Heads", "Southport"],
    marketInsights: [
      {
        title: "Dual Market Opportunity",
        detail: "The Gold Coast supports both permanent and short-term rental strategies. We help investors navigate both markets, advising on optimal strategy based on your property type, location, and financial objectives.",
      },
      {
        title: "Coomera & Northern Growth Corridor",
        detail: "The northern Gold Coast — particularly Coomera, Hope Island, and Pimpama — is one of Australia's fastest-growing residential areas with major schools, retail, and transport infrastructure under development.",
      },
      {
        title: "Health & Education Precincts",
        detail: "Gold Coast University Hospital and Griffith University generate significant healthcare and academic professional rental demand in Southport, Parkwood, and Varsity Lakes.",
      },
      {
        title: "2032 Olympics Legacy",
        detail: "The Gold Coast's 2018 Commonwealth Games infrastructure, combined with planned 2032 Olympic venue investments, underpins long-term property value and tourism-driven rental demand.",
      },
    ],
    whyUs:
      "The Gold Coast's complexity — from holiday let regulations to body corporate compliance in high-density towers — demands experienced management. We handle it all: from beachside apartments to family homes in the growth corridors, with full compliance assurance.",
  },

  "gympie": {
    slug: "gympie",
    name: "Gympie",
    region: "Wide Bay–Burnett",
    tagline: "The Mary Valley's gateway city — an affordable, tight rental market with growing commuter appeal.",
    heroImage: "https://images.unsplash.com/photo-1500534314209-a25ddb2bd429?w=1920&q=80",
    marketStats: {
      avgRentalYield: "6.2%",
      vacancyRate: "1.6%",
      medianRent: "$420/wk",
      medianHousePrice: "$490,000",
    },
    overview:
      "Gympie is the commercial and services centre of the Wide Bay hinterland — and one of Queensland's most underappreciated investment markets. Located midway between Brisbane and the Sunshine Coast on the Bruce Highway, Gympie has emerged as a commuter and lifestyle option for families priced out of coastal markets, driving consistent rental demand at price points that still deliver strong yields. One Property Australia manages residential portfolios across Gympie and the Mary Valley including Rainbow Beach and Tin Can Bay coastal areas.",
    keySuburbs: ["South Gympie", "Monkland", "Gympie CBD", "Two Mile", "Southside", "Chatsworth", "Imbil"],
    marketInsights: [
      {
        title: "Bruce Highway Commuter Appeal",
        detail: "Gympie's position on the Bruce Highway — approximately 90 minutes from Brisbane and 60 minutes from Maroochydore — makes it viable for commuters and remote workers seeking affordability without isolation.",
      },
      {
        title: "Affordability Entry Point",
        detail: "With median house prices well below Sunshine Coast levels and rental yields exceeding 6%, Gympie offers investors a compelling value proposition in a tight market.",
      },
      {
        title: "Resource & Agricultural Economy",
        detail: "The Mary Valley region's timber, agriculture, and small-scale mining activity provides stable local employment beyond the service sector, broadening the rental tenant base.",
      },
      {
        title: "Coastal Satellite — Rainbow Beach & Tin Can Bay",
        detail: "The coastal communities accessible from Gympie generate seasonal holiday rental demand and attract permanent residents seeking an alternative lifestyle to Sunshine Coast pricing.",
      },
    ],
    whyUs:
      "Gympie's market rewards landlords who manage proactively. In a smaller market, our tenant relationships and maintenance networks mean we resolve issues quickly — protecting your property and your tenants' satisfaction in a community where word of mouth matters.",
  },

  "gladstone": {
    slug: "gladstone",
    name: "Gladstone",
    region: "Central Queensland",
    tagline: "Australia's industrial port powerhouse — a high-yield market driven by energy and resources.",
    heroImage: "https://images.unsplash.com/photo-1574715651341-9d08c8e1efc0?w=1920&q=80",
    marketStats: {
      avgRentalYield: "6.8%",
      vacancyRate: "2.1%",
      medianRent: "$480/wk",
      medianHousePrice: "$400,000",
    },
    overview:
      "Gladstone is the industrial heart of Central Queensland — home to Australia's largest multi-commodity export port, two LNG processing facilities on Curtis Island, a major aluminium smelter, and Queensland's largest power station. This industrial base creates consistent demand for both corporate and residential accommodation. One Property Australia manages residential portfolios across Gladstone Region including Calliope, Tannum Sands, and Agnes Water–Town of 1770.",
    keySuburbs: ["South Gladstone", "Kin Kora", "West Gladstone", "Toolooa", "New Auckland", "Tannum Sands", "Calliope"],
    marketInsights: [
      {
        title: "LNG & Resources Employment",
        detail: "The Curtis Island LNG facilities — operated by Santos, Shell, and Origin — employ thousands of FIFO and residential workers, generating strong demand for quality corporate and family accommodation.",
      },
      {
        title: "Industrial Workforce Demand",
        detail: "Boyne Smelters, Gladstone Power Station, and QAL maintain large permanent workforces who require quality long-term rental accommodation — typically high-quality, stable tenancies.",
      },
      {
        title: "Port Expansion",
        detail: "Gladstone Ports Corporation continues to invest in port capacity expansion to handle increased coal, LNG, and agricultural exports — a structural driver of long-term employment and rental demand.",
      },
      {
        title: "Coastal Satellite Markets",
        detail: "Tannum Sands and Agnes Water–Town of 1770 provide lifestyle diversification within the Gladstone Region, with growing holiday and permanent rental demand from those priced out of coastal tourist hotspots.",
      },
    ],
    whyUs:
      "Gladstone's industrial tenant base has specific needs — corporate lease compliance, condition reports that meet employer standards, and maintenance response times that retain high-income tenants. We are experienced in managing properties for resources sector tenants and deliver the professional management their employers expect.",
  },
};

export const allCities = Object.values(cityData);
